import React, { useState } from 'react';
import { X, MapPin, Maximize2, Edit2, Map as MapIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { cn } from '@/utils/cn';

interface ZoneDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
}

export function ZoneDetailDrawer({ isOpen, onClose, data }: ZoneDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState('info');

  if (!data) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="md" position="right" hideCloseButton>
      <div className="h-full flex flex-col bg-white">
        {/* Header */}
        <div className="p-6 pb-4 border-b border-slate-100 relative">
          <button 
            onClick={onClose}
            className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="pr-12">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-normal text-slate-900">{data.zone_name}</h2>
              <span className={cn(
                "px-2 py-0.5 rounded text-[11px] font-normal border",
                data.zone_type === 'construction' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'
              )}>
                {data.zone_type === 'construction' ? 'Thi công' : 'Bảo dưỡng'}
              </span>
            </div>
            <p className="sm:text-sm text-xs text-slate-500">
              Mã khu vực: <span className="font-normal text-slate-700">{data.zone_code || '-'}</span>
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-6 mt-6 border-b border-slate-100">
            {[
              { id: 'info', label: 'Thông tin chung' },
              { id: 'map', label: 'Bản đồ' },
              { id: 'history', label: 'Lịch sử hoạt động' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "pb-3 sm:text-sm text-xs font-normal transition-colors relative",
                  activeTab === tab.id ? "text-emerald-600" : "text-slate-500 hover:text-slate-700"
                )}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Mini Map */}
              <div className="relative h-48 rounded-md overflow-hidden border border-slate-200">
                <div className="absolute inset-0 bg-[url('https://maps.googleapis.com/maps/api/staticmap?center=10.823456,106.629123&zoom=17&size=600x300&maptype=satellite')] bg-cover bg-center" />
                <div className="absolute inset-0 bg-emerald-500/20" style={{ clipPath: 'polygon(30% 20%, 80% 30%, 90% 70%, 70% 90%, 20% 80%)' }}>
                  <div className="w-full h-full border-2 border-emerald-400" />
                </div>
                
                {/* Map Controls */}
                <div className="absolute right-2 top-2 flex flex-col gap-1 bg-white rounded border border-slate-200">
                  <button className="p-1.5 text-slate-600 hover:text-emerald-600 border-b border-slate-100">+</button>
                  <button className="p-1.5 text-slate-600 hover:text-emerald-600">-</button>
                </div>
                <button className="absolute right-2 bottom-2 p-1.5 bg-white rounded border border-slate-200 text-slate-600 hover:text-emerald-600">
                  <Maximize2 className="w-4 h-4" />
                </button>
              </div>

              {/* Info Grid */}
              <div className="space-y-4">
                <div className="grid grid-cols-[140px_1fr] gap-4">
                  <span className="sm:text-sm text-xs text-slate-500">Dự án / Công việc</span>
                  <span className="sm:text-sm text-xs text-slate-900 font-normal">{data.project?.name || '-'}</span>
                </div>
                <div className="grid grid-cols-[140px_1fr] gap-4">
                  <span className="sm:text-sm text-xs text-slate-500">Khu vực cha</span>
                  <span className="sm:text-sm text-xs text-slate-900">{data.parent_zone?.zone_name || '-'}</span>
                </div>
                <div className="grid grid-cols-[140px_1fr] gap-4">
                  <span className="sm:text-sm text-xs text-slate-500">Diện tích</span>
                  <span className="sm:text-sm text-xs text-slate-900">{data.area_m2?.toLocaleString('vi-VN') || '0'} m²</span>
                </div>
                <div className="grid grid-cols-[140px_1fr] gap-4">
                  <span className="sm:text-sm text-xs text-slate-500">Loại khu vực</span>
                  <span className="sm:text-sm text-xs text-slate-900">{data.zone_type === 'construction' ? 'Thi công' : 'Bảo dưỡng'}</span>
                </div>
                <div className="grid grid-cols-[140px_1fr] gap-4 items-center">
                  <span className="sm:text-sm text-xs text-slate-500">Trạng thái</span>
                  <div>
                    <StatusBadge 
                      label={data.status_code === 'active' ? 'Đang hoạt động' : data.status_code === 'paused' ? 'Tạm dừng' : 'Đã hoàn thành'} 
                      variant={data.status_code === 'active' ? 'success' : data.status_code === 'paused' ? 'warning' : 'purple'} 
                      className="text-[11px] px-2 py-0.5" 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-[140px_1fr] gap-4">
                  <span className="sm:text-sm text-xs text-slate-500">Mô tả</span>
                  <span className="sm:text-sm text-xs text-slate-900 leading-relaxed">
                    {data.description || 'Chưa có mô tả.'}
                  </span>
                </div>
                <div className="grid grid-cols-[140px_1fr] gap-4">
                  <span className="sm:text-sm text-xs text-slate-500">Cập nhật cuối</span>
                  <span className="sm:text-sm text-xs text-slate-900">{data.updated_at ? new Date(data.updated_at).toLocaleString('vi-VN') : '-'}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab !== 'info' && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-400">
                <MapPin className="w-8 h-8" />
              </div>
              <p className="sm:text-sm text-xs text-slate-900 font-normal">Tính năng đang phát triển</p>
              <p className="text-xs text-slate-500 mt-1">Thông tin chi tiết sẽ sớm được cập nhật.</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <Button variant="outline" className="h-9 px-4 sm:text-sm text-xs text-slate-600 bg-white">
            <Edit2 className="w-4 h-4 mr-2" />
            Chỉnh sửa
          </Button>
          <Button variant="outline" className="h-9 px-4 sm:text-sm text-xs text-slate-600 bg-white">
            <MapIcon className="w-4 h-4 mr-2" />
            Xem trên bản đồ
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
